import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Select from "react-select";
import { createSelector } from "reselect";
import * as Yup from "yup";
import { useFormik } from "formik";
import {Label, Form, Modal, ModalHeader, ModalBody, Input, ModalFooter} from 'reactstrap';
import ClubLogo from "./Common/ClubLogo";
import {validators} from "../helpers/ValidatorHelper";
import {updateEvent} from "../slices/event/details/thunk";
import {OptionsNumerous} from "../helpers/Tools";
import {typeKaraoke, eventTypeLabels, typeStandardClosed, defaultColorOpacity} from "./constants/default";
import {viewEventSongs} from "../slices/event/view/thunk";
import { eventUpdateReset } from "../slices/event/details/reducer";
import Spinner from "./Common/Spinner";
import Loader from "./Common/Loader";
import { fetchSongLibraries } from "../slices/songLibrary/thunk";
import { selectAllLibraries } from "../slices/songLibrary/reducer";

// Ten sam wzór co useUserColors — podgląd 1:1 odzwierciedla pasek wydarzenia.
const hexToRgba = (hex, alpha) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  // Brak koloru (reset) => domyślny success z motywu, tak jak w useUserColors.
  if (!m) return `rgba(var(--vz-success-rgb), ${alpha})`;
  return `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${alpha})`;
};

const EventHeader = ({isEventAdmin, event}) => {
  if (Object.keys(event).length === 0) {
    return <Loader/>;
  }
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { eventId } = useParams();
  const eventName = event.name === 'Your event name' ? t(event.name) : event.name;
  const description = event.description;
  const userName = event?.clubAdmin?.username;
  const instagramLink = event?.clubAdmin?.instagramLink;
  const facebookLink = event?.clubAdmin?.facebookLink;
  const websiteLink = event?.clubAdmin?.websiteLink;
  const youtubeLink = event?.clubAdmin?.youtubeLink;
  const tiktokLink = event?.clubAdmin?.tiktokLink;
  const eventType = event.type;

  // console.log(event?.clubAdmin);
  const [eventDetailsButtonCollapse, setEventDetailsButtonCollapse] = useState(false);
  const toggleEventDetailsButtonCollapse = () => {setEventDetailsButtonCollapse(!eventDetailsButtonCollapse);};

  const selectEventDetails = createSelector(
    (state) => state.Event,
    (slice) => ({
      updateEventErrors: slice.violations,
      updateEventSuccess: slice.updateSuccess,
    })
  );
  const {
    updateEventErrors,
    updateEventSuccess
  } = useSelector(selectEventDetails);

  const libraries = useSelector(selectAllLibraries);

  // Minimalna kwota wymaganego napiwku: 2, 5, potem 10, 20, ... 200 zł
  const minTipAmountOptions = [2, 5, ...Array.from({ length: 20 }, (_, i) => (i + 1) * 10)]
    .map((amount) => ({ value: amount, label: String(amount) }));

  // Limit dedykacji na gościa: 1-20, zgodnie z walidacją po stronie API.
  const dedicationLimitOptions = Array.from({ length: 20 }, (_, i) => ({
    value: i + 1,
    label: String(i + 1),
  }));

  useEffect(() => {
    if (isEventAdmin) {
      dispatch(fetchSongLibraries());
    }
  }, [dispatch, isEventAdmin]);

  useEffect(() => {
    if (updateEventErrors?.length > 0) {
      updateEventErrors.forEach((violation) => {
        eventEditForm.setFieldError(violation.propertyPath, t(violation.message));
      });
    }
  }, [updateEventErrors]);

  useEffect(() => {
    if (updateEventSuccess) {
      dispatch(eventUpdateReset())
      dispatch(viewEventSongs(eventId));
      setEventDetailsButtonCollapse(false)
    }
  }, [updateEventSuccess]);

  const eventEditForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: eventName,
      description: event.description || '',
      type: eventType || '',
      limitSongsOnList: event.limitSongsOnList,
      limitSongsInLine: event.limitSongsInLine,
      limitSongsPerUser: event.limitSongsPerUser,
      allowAddSongs: event.allowAddSongs,
      allowDuplicateInList: event.allowDuplicateInList,
      partiallyHiddenForGuests: event.partiallyHiddenForGuests,
      guestVisibleSongsCount: event.guestVisibleSongsCount || 3,
      allowGuestsAddSongsFromDatabaseOutside: event.allowGuestsAddSongsFromDatabaseOutside,
      loadFullSongDatabase: event.loadFullSongDatabase,
      songLibrary: event.songLibrary || null,
      requireTip: event.requireTip || false,
      minTipAmount: event.minTipAmount || 10,
      allowDedications: event.allowDedications || false,
      limitDedicationsPerUser: event.limitDedicationsPerUser || 3,
      primaryColor: event.primaryColor || null,
      primaryColorOpacity: (event.primaryColorOpacity === null || event.primaryColorOpacity === undefined) ? defaultColorOpacity : event.primaryColorOpacity,
    },
    validationSchema: Yup.object({
      name: validators.eventName(t),
      type: Yup.string().required(t("Please Enter Type")),
    }),
    onSubmit: async (values) => {
      dispatch(updateEvent(eventId, values));
    }
  });

  return (
    <>
      {!isEventAdmin && eventName && <div
        className="accordion-icon-none accordion">
        <div className="accordion-item border-0 bg-transparent">
          <h2 className="accordion-header">
            <div className="accordion-button collapsed p-1 bg-color-primary-subtle" style={{ borderRadius: '8px' }}>
              <ClubLogo
                eventName={eventName}
                description={description}
                instagramLink={instagramLink}
                facebookLink={facebookLink}
                websiteLink={websiteLink}
                youtubeLink={youtubeLink}
                tiktokLink={tiktokLink}
                userName={userName}
              />
            </div>
          </h2>
        </div>
      </div>
      }
      {isEventAdmin && eventName && <>
        <div className="accordion-icon-none accordion">
          <div className="accordion-item border-0 bg-transparent">
            <h2 className="accordion-header">
              {/* Sama belka nie jest klikalna — w środku siedzą linki do social
                  mediów i wgrywanie logo, więc otwieranie ustawień z całego
                  obszaru łapało te kliknięcia. Ustawienia otwierają kropki. */}
              <div
                className="accordion-button collapsed icon-color-primary pt-1 pb-1 pe-2 bg-color-primary-subtle"
                style={{ borderRadius: '8px', cursor: 'default' }}
              >
                <ClubLogo
                  eventName={eventName}
                  description={description}
                  instagramLink={instagramLink}
                  facebookLink={facebookLink}
                  websiteLink={websiteLink}
                  youtubeLink={youtubeLink}
                  tiktokLink={tiktokLink}
                  userName={userName}
                />
                <button
                  type="button"
                  className="btn btn-link p-0 ms-auto flex-shrink-0 icon-color-primary lh-1"
                  onClick={toggleEventDetailsButtonCollapse}
                  title={t('Event Settings')}
                  aria-label={t('Event Settings')}
                >
                  <i
                    className="ri-more-2-fill icon-color-primary"
                    style={{ fontSize: '2rem' }}
                  ></i>
                </button>
              </div>
            </h2>
          </div>
        </div>
        {/* Ustawienia w modalu: lista utworów zostaje na swoim miejscu, a długi
            formularz nie spycha jej poza ekran. */}
        <Modal isOpen={eventDetailsButtonCollapse} toggle={toggleEventDetailsButtonCollapse} size="lg" scrollable>
          <ModalHeader toggle={toggleEventDetailsButtonCollapse}>{t('Event Settings')}</ModalHeader>
              {/* Formularz siedzi między .modal-content a .modal-body, więc bez tego
                  własnego flexa body nie ma się do czego skurczyć i przewijanie
                  Bootstrapa (modal scrollable) nie działa - na telefonie ustawienia
                  urywały się na dole. */}
              <Form
                className="tablelist-form d-flex flex-column"
                style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}
                onSubmit={(e) => {
                  e.preventDefault();
                  eventEditForm.handleSubmit();
                }}
              >
                <ModalBody style={{ overflowY: 'auto', minHeight: 0 }}>
                  <div className="mb-2">
                    <Label htmlFor="name-field" className="form-label">{t("Name")}</Label>
                    <Input
                      className={`form-control ${eventEditForm.touched.name && eventEditForm.errors.name ? 'is-invalid' : ''}`}
                      type="text" id="name" name="name" placeholder={t("Enter Name")}
                      onChange={eventEditForm.handleChange}
                      onBlur={eventEditForm.handleBlur}
                      value={eventEditForm.values.name}/>
                    {eventEditForm.touched.name && eventEditForm.errors.name && (
                      <div className="invalid-feedback">{eventEditForm.errors.name}</div>
                    )}
                  </div>

                  <div className="mb-2">
                    <Label htmlFor="description" className="form-label">{t("Event Description")}</Label>
                    <Input
                      className={`form-control ${eventEditForm.touched.description && eventEditForm.errors.description ? 'is-invalid' : ''}`}
                      type="textarea" id="description" name="description" rows="4"
                      placeholder={t("Enter event description")}
                      onChange={eventEditForm.handleChange}
                      onBlur={eventEditForm.handleBlur}
                      value={eventEditForm.values.description}/>
                    {eventEditForm.touched.description && eventEditForm.errors.description && (
                      <div className="invalid-feedback">{eventEditForm.errors.description}</div>
                    )}
                  </div>

                  <div className="mb-2">
                    <Label htmlFor="type-field" className="form-label">{t("Type")}</Label>
                    <select
                      className={`form-control ${eventEditForm.touched.type && eventEditForm.errors.type ? 'is-invalid' : ''}`}
                      name="type" id="type"
                      value={eventEditForm.values.type}
                      onChange={eventEditForm.handleChange}
                      onBlur={eventEditForm.handleBlur}
                    >
                      {Object.entries(eventTypeLabels).map(([value, label]) => (
                          <option key={value} value={value}>{t(label)}</option>
                        )
                      )}
                    </select>
                    {eventEditForm.touched.type && eventEditForm.errors.type && (
                      <div className="invalid-feedback">{eventEditForm.errors.type}</div>
                    )}
                  </div>

                  <div className="mb-2">
                    <Label htmlFor="songLibrary-field" className="form-label">{t("Song Library")}</Label>
                    <select
                      className="form-control"
                      name="songLibrary"
                      id="songLibrary"
                      value={eventEditForm.values.songLibrary ? (typeof eventEditForm.values.songLibrary === 'string' ? eventEditForm.values.songLibrary : eventEditForm.values.songLibrary['@id'] || `/api/song_libraries/${eventEditForm.values.songLibrary.id}`) : ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : e.target.value;
                        eventEditForm.setFieldValue('songLibrary', value);
                      }}
                      onBlur={eventEditForm.handleBlur}
                    >
                      <option value="">{t("Global Library")}</option>
                      {libraries.map((library) => (
                        <option key={library.id} value={`/api/song_libraries/${library.id}`}>
                          {library.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="row row-cols-sm-auto g3">
                    <div className="mb-2">
                      <Label htmlFor="limitSongsOnList"
                             className="form-label text-muted">{t('Limit Of Songs On The List')}</Label>
                      <Select
                        name="limitSongsOnList"
                        id="limitSongsOnList"
                        value={{
                          value: eventEditForm.values.limitSongsOnList,
                          label: String(eventEditForm.values.limitSongsOnList)
                        }}
                        onChange={(newValue) => {
                          eventEditForm.setFieldValue('limitSongsOnList', newValue.value);
                        }}
                        onBlur={eventEditForm.handleBlur}
                        options={OptionsNumerous(51)}
                      />
                    </div>

                    <div className="mb-2">
                      <Label htmlFor="choices-single-default" className="form-label text-muted">{eventEditForm.values.type === typeKaraoke ? t('Limit Of Songs On The List Per User') : t('Limit Of Votes Per User')}</Label>
                      <Select name="limitSongsPerUser" id="limitSongsPerUser"
                              value={{
                                value: eventEditForm.values.limitSongsPerUser,
                                label: String(eventEditForm.values.limitSongsPerUser)
                              }}
                              onChange={(newValue) => {
                                eventEditForm.setFieldValue('limitSongsPerUser', newValue.value);
                              }}
                              onBlur={eventEditForm.handleBlur}
                              options={OptionsNumerous(12, 1)}
                      />
                    </div>
                    <div className="mb-2 form-check form-switch form-switch-md">
                      <Input className="form-check-input" type="checkbox" role="switch" id="partiallyHiddenForGuests"
                             onChange={eventEditForm.handleChange}
                             onBlur={eventEditForm.handleBlur}
                             checked={eventEditForm.values.partiallyHiddenForGuests}
                      />
                      <Label className="form-check-label" htmlFor="partiallyHiddenForGuests">
                        {t('List partially hidden for guests')}
                      </Label>
                    </div>
                    {eventEditForm.values.partiallyHiddenForGuests && (
                      <div className="mb-2">
                        <Label htmlFor="guestVisibleSongsCount" className="form-label text-muted">{t('Number of songs visible to guests')}</Label>
                        <Select
                          name="guestVisibleSongsCount"
                          id="guestVisibleSongsCount"
                          value={{
                            value: eventEditForm.values.guestVisibleSongsCount,
                            label: String(eventEditForm.values.guestVisibleSongsCount)
                          }}
                          onChange={(newValue) => {
                            eventEditForm.setFieldValue('guestVisibleSongsCount', newValue.value);
                          }}
                          onBlur={eventEditForm.handleBlur}
                          options={OptionsNumerous(9, 2)}
                        />
                      </div>
                    )}
                    {eventEditForm.values.type === typeKaraoke && (
                      <>
                        <div className="mb-3">
                          <Label htmlFor="choices-single-default" className="form-label text-muted">{t('Limit Of Songs In Sequence Per User')}</Label>
                          <Select
                            value={{
                              value: eventEditForm.values.limitSongsInLine,
                              label: String(eventEditForm.values.limitSongsInLine)
                            }}
                            onChange={(newValue) => {
                              eventEditForm.setFieldValue('limitSongsInLine', newValue.value);
                            }}
                            onBlur={eventEditForm.handleBlur}
                            options={OptionsNumerous(3, 1)}
                          />
                        </div>
                        <div className="mb-2 form-check form-switch form-switch-md">
                          <Input className="form-check-input" type="checkbox" role="switch" id="allowDuplicateInList"
                                 onChange={eventEditForm.handleChange}
                                 onBlur={eventEditForm.handleBlur}
                                 checked={eventEditForm.values.allowDuplicateInList}
                          />
                          <Label className="form-check-label" htmlFor="allowDuplicateInList">
                            {t('Allow duplicate In List')}
                          </Label>
                        </div>
                      </>
                    )}
                    {eventEditForm.values.type !== typeStandardClosed && (<div className="mb-2 form-check form-switch form-switch-md">
                      <Input className="form-check-input" type="checkbox" role="switch" id="allowAddSongs"
                             onChange={eventEditForm.handleChange}
                             onBlur={eventEditForm.handleBlur}
                             checked={eventEditForm.values.allowAddSongs}
                      />
                      <Label className="form-check-label" htmlFor="allowAddSongs">
                        {t('Allow Add Songs')}
                      </Label>
                    </div>)}
                    <div className="mb-2 form-check form-switch form-switch-md">
                      <Input className="form-check-input" type="checkbox" role="switch" id="allowGuestsAddSongsFromDatabaseOutside"
                             onChange={eventEditForm.handleChange}
                             onBlur={eventEditForm.handleBlur}
                             checked={eventEditForm.values.allowGuestsAddSongsFromDatabaseOutside}
                      />
                      <Label className="form-check-label" htmlFor="allowGuestsAddSongsFromDatabaseOutside">
                        {t('Allow Guests Add Songs From Database Outside')}
                      </Label>
                    </div>
                    <div className="mb-2 form-check form-switch form-switch-md">
                      <Input className="form-check-input" type="checkbox" role="switch" id="loadFullSongDatabase"
                             onChange={eventEditForm.handleChange}
                             onBlur={eventEditForm.handleBlur}
                             checked={eventEditForm.values.loadFullSongDatabase}
                      />
                      <Label className="form-check-label" htmlFor="loadFullSongDatabase">
                        {t('Load Full Song Database')}
                        {' '}
                        <i
                          className="ri-question-line text-primary"
                          style={{ cursor: 'pointer', fontSize: '1.1rem', fontWeight: 'bold' }}
                          title={t('Load Full Song Database Tooltip')}
                        ></i>
                      </Label>
                    </div>
                    {eventEditForm.values.type !== typeKaraoke && (
                      <>
                        <div className="mb-2 form-check form-switch form-switch-md">
                          <Input className="form-check-input" type="checkbox" role="switch" id="requireTip"
                                 disabled={!event.allowTips}
                                 onChange={eventEditForm.handleChange}
                                 onBlur={eventEditForm.handleBlur}
                                 checked={eventEditForm.values.requireTip}
                          />
                          <Label className="form-check-label" htmlFor="requireTip">
                            {t('Required tip')}
                            {!event.allowTips && (
                              <>
                                {' '}
                                <i
                                  className="ri-question-line text-primary"
                                  style={{ cursor: 'pointer', fontSize: '1.1rem', fontWeight: 'bold' }}
                                  title={t('Enable tips first to require a tip')}
                                ></i>
                              </>
                            )}
                          </Label>
                        </div>
                        {event.allowTips && eventEditForm.values.requireTip && (
                          <div className="mb-2">
                            <Label htmlFor="minTipAmount" className="form-label text-muted">{t('Minimum tip amount')}</Label>
                            <Select
                              name="minTipAmount"
                              id="minTipAmount"
                              value={{
                                value: eventEditForm.values.minTipAmount,
                                label: String(eventEditForm.values.minTipAmount)
                              }}
                              onChange={(newValue) => {
                                eventEditForm.setFieldValue('minTipAmount', newValue.value);
                              }}
                              onBlur={eventEditForm.handleBlur}
                              options={minTipAmountOptions}
                            />
                          </div>
                        )}
                        <div className="mb-2 form-check form-switch form-switch-md">
                          <Input className="form-check-input" type="checkbox" role="switch" id="allowDedications"
                                 onChange={eventEditForm.handleChange}
                                 onBlur={eventEditForm.handleBlur}
                                 checked={eventEditForm.values.allowDedications}
                          />
                          <Label className="form-check-label" htmlFor="allowDedications">
                            {t('Allow dedications')}
                            {' '}
                            <i
                              className="ri-question-line text-primary"
                              style={{ cursor: 'pointer', fontSize: '1.1rem', fontWeight: 'bold' }}
                              title={t('Allow dedications tooltip')}
                            ></i>
                          </Label>
                        </div>
                        {eventEditForm.values.allowDedications && (
                          <div className="mb-2">
                            <Label htmlFor="limitDedicationsPerUser" className="form-label text-muted">
                              {t('Dedication limit per guest')}
                            </Label>
                            <Select
                              name="limitDedicationsPerUser"
                              id="limitDedicationsPerUser"
                              value={{
                                value: eventEditForm.values.limitDedicationsPerUser,
                                label: String(eventEditForm.values.limitDedicationsPerUser)
                              }}
                              onChange={(newValue) => {
                                eventEditForm.setFieldValue('limitDedicationsPerUser', newValue.value);
                              }}
                              onBlur={eventEditForm.handleBlur}
                              options={dedicationLimitOptions}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <hr className="my-3" />

                  <div className="mb-2">
                    <Label className="form-label">{t("Event Bar Background Color")}</Label>
                    <div className="d-flex align-items-center gap-2">
                      <Input
                        type="color" className="form-control form-control-color w-25"
                        value={eventEditForm.values.primaryColor || '#0ab39c'}
                        onChange={(e) => eventEditForm.setFieldValue('primaryColor', e.target.value)}
                      />
                      <button
                        type="button" className="btn btn-soft-danger btn-sm"
                        onClick={() => eventEditForm.setFieldValue('primaryColor', null)}
                        title={t('Reset color')}
                      >
                        <i className="ri-close-circle-line"></i> {t('Reset')}
                      </button>
                    </div>
                  </div>

                  <div className="mb-2">
                    <Label className="form-label">{t("Background Transparency")}</Label>
                    <div className="d-flex align-items-center gap-2">
                      <Input
                        type="range" className="form-range"
                        min="0" max="1" step="0.05"
                        value={eventEditForm.values.primaryColorOpacity}
                        onChange={(e) => eventEditForm.setFieldValue('primaryColorOpacity', parseFloat(e.target.value))}
                      />
                      <span className="text-muted" style={{ minWidth: '3.5ch' }}>
                        {Math.round((eventEditForm.values.primaryColorOpacity ?? defaultColorOpacity) * 100)}%
                      </span>
                    </div>
                  </div>

                  <div className="mb-2">
                    <Label className="form-label">{t("Preview")}</Label>
                    <div
                      className="d-flex align-items-center fw-medium px-3"
                      style={{
                        backgroundColor: hexToRgba(eventEditForm.values.primaryColor, eventEditForm.values.primaryColorOpacity ?? defaultColorOpacity),
                        border: `2px solid ${hexToRgba(eventEditForm.values.primaryColor, Math.min((eventEditForm.values.primaryColorOpacity ?? defaultColorOpacity) + 0.4, 1))}`,
                        borderRadius: '8px',
                        minHeight: '48px',
                      }}
                    >
                      {eventName}
                    </div>
                  </div>
                </ModalBody>
                <ModalFooter>
                  <div className="hstack gap-2 justify-content-end">
                    <button type="button" className="btn btn-label btn-light" onClick={toggleEventDetailsButtonCollapse}>
                      <i className="ri-close-circle-line label-icon align-middle fs-16 me-2"></i>
                      {t('Close')}
                    </button>
                    <button type="submit" className="btn btn-label btn-success color-primary" id="add-btn">
                      <i className="ri-check-double-line label-icon align-middle fs-16 me-2"></i>
                      {t('Save')}
                    </button>
                  </div>
                </ModalFooter>
              </Form>
        </Modal>
      </>
      }
    </>
  );
};

export default EventHeader;
